import { AppAction, AppContext } from "helpers/context";
import { MapContext } from "helpers/map";
import { useContext, useEffect, useRef } from "react";
import { RightPanel, zoomLevelMedium } from "helpers/constants";
import { getMarkerUrl, formatKM, getStyleIndex, styleArr, getLocalScaleLocations } from "helpers/helpers";

interface MapMultipleMarkerProps {
    locations: any[];
    total?: number;
    min?: number;
}

const MapMultipleMarker = (props: Readonly<MapMultipleMarkerProps>) => {
    const [context, dispatch] = useContext(AppContext);
    const { AMap, map } = useContext(MapContext);

    // refs
    const clusterRef = useRef<any>(null);
    const pointsRef = useRef<any[]>([]);

    // 常量配置，不影响业务逻辑
    const GRID_SIZE = 80;

    const unit = context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations";
    const isNumberOfLocations = unit === "numberOfLocations";

    const handleMarkerClick = (e: any) => {
        if (e.clusterData.length > 1) {
            map.setCenter([e.lnglat.lng, e.lnglat.lat]);
            map.zoomIn();
            return;
        }

        const data = e.clusterData[0] ? e.clusterData[0] : e.cluster.p;

        if (context.currentMultipleLocationInfo && data.id === context.currentMultipleLocationInfo.id) {
            dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
            dispatch({ type: AppAction.setCurrentMultipleLocationInfo, payload: undefined });
            return;
        }

        dispatch({ type: AppAction.setSearchMapCenter, payload: undefined });
        dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
        dispatch({ type: AppAction.setCurrentMultipleLocationInfo, payload: data });
        dispatch({ type: AppAction.setMapZoom, payload: zoomLevelMedium });
        dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Single });
    };

    /** 计算聚合点样式，保证样式稳定 */
    const renderClusterMarker = (event: any) => {
        let index = 0;
        let count = 0;

        if (isNumberOfLocations) {
            count = event.count;
            index = getStyleIndex(event.count, context.currentMultipleLocations?.itemCount ?? 0);
        } else {
            // 对聚合源数据做数值规整，统一使用 Math.round
            const clusterData = event.clusterData;
            let allOriginData: any[] = [];

            for (const clusterItem of clusterData) {
                const amapMarker = clusterItem._amapMarker;
                if (amapMarker && Array.isArray(amapMarker.originData)) {
                    allOriginData = allOriginData.concat(amapMarker.originData);
                }
            }

            allOriginData = allOriginData.flat();

            allOriginData.forEach((item: any) => {
                const value = item[unit] ?? 0;
                const num = typeof value === "string" ? Number(value) : value;
                if (Number.isFinite(num)) {
                    count += Math.round(num);
                }
            });

            index = getStyleIndex(count, props.total || 0, props.min);
        }

        const bubble = `<div
                style="
                text-align:center;
                opacity:0.9;
                font-size:14px;
                font-weight:500;
                color:#fff;
                border:3px solid #fff;
                border-radius:50%;
                box-shadow:0 0 5px 0 rgba(0,0,0,0.3);
                background:${styleArr[index].bg};
                width:${styleArr[index].size};
                height:${styleArr[index].size};
                line-height:${styleArr[index].size};
                "
            >${formatKM(count)}</div>`;

        event.marker.setContent(bubble);
        event.marker.setAnchor('center');
        event.marker.setOffset(new AMap.Pixel(0, -15));
    };

    /** 计算单点样式，保证显示一致 */
    const renderMarker = (event: any) => {
        const data = event.data[0];
        let index = 0;
        let color = "#fff8a1";
        if (!isNumberOfLocations) {
            if (context.scaleType === "local") {
                let scaleArr = getLocalScaleLocations(pointsRef.current, map);
                let min = Infinity;
                let max = -Infinity;
                scaleArr.forEach((it: any) => {
                    const v = Math.round(it[unit]);
                    if (v < min) min = v;
                    if (v > max) max = v;
                });
                const hasValidMin = min !== Infinity && max !== -Infinity;
                const range = max - min;
                const rangeIsEnough = range > 9;
                if (hasValidMin && rangeIsEnough) {
                    index = getStyleIndex(data[unit], max, min);
                    color = styleArr[index].bg;
                } else {
                    color = "#001789";
                }
            } else {
                index = getStyleIndex(data[unit], props.total || 0, props.min);
                color = styleArr[index].bg;
            }
        }

        const url = getMarkerUrl(color);

        const isActive =
            context.currentMultipleLocationInfo && data.id === context.currentMultipleLocationInfo.id;

        const content = `<img src="${url}" style="width:100%;height:100%;" />`;
        event.marker.setContent(content);
        event.marker.setSize(isActive ? new AMap.Size(38, 63) : new AMap.Size(19, 32));
        event.marker.setAnchor('center');
        event.marker.setOffset(isActive ? new AMap.Pixel(0, -30) : new AMap.Pixel(0, -15));
    };

    useEffect(() => {
        const items = props.locations ?? [];
        if (items.length === 0) return;

        // 只保留 checked 点位
        pointsRef.current = [];

        items.forEach((location: any) => {
            pointsRef.current.push({
                ...location,
                lnglat: [location.longitude, location.latitude],
            });
        });

        // 创建聚合图层
        clusterRef.current = new AMap.MarkerCluster(map, pointsRef.current, {
            gridSize: GRID_SIZE,
            renderClusterMarker,
            renderMarker,
            // maxZoom: 6.33, // 历史最大显示级别
            maxZoom: 11.63 // 当前最大显示级别
        });

        clusterRef.current.on("click", handleMarkerClick);

        return () => {
            // 清理
            if (clusterRef.current) {
                clusterRef.current.off("click", handleMarkerClick);
                clusterRef.current.setMap(null);
                clusterRef.current = null;
            }
        };
    }, [props.locations, context.currentMultipleLocations?.items, context.currentMultipleLocationInfo, context.scaleType, isNumberOfLocations, unit, map]);

    return null;
};

export default MapMultipleMarker;


